import { BookingGuestsPanel, CourseCreditTypeName, CourseParticipationInfo } from './course-participation'
import { CancelBookingConfirmDialog } from './cancel-booking-confirm-dialog'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, isAfter } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, CalendarPlusIcon, ChevronDown, ClockIcon, CoinsIcon, Loader2Icon, MapPin, UsersIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { downloadIcsFile } from '@/lib/calendar-service'
import { formatCourseCredits, formatCredits } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'

export type SessionDetailData = {
    bookingId?: string
    sessionId?: string
    courseName: string
    courseDescription?: string | null
    startsAt: string
    endsAt: string
    trainerName?: string | null
    trainerId?: string | null
    level?: string | null
    availableSpots?: number | null
    maxParticipants?: number | null
    status?: 'confirmed' | 'cancelled' | null
    registrationUntil?: string | null
    cancellationUntil?: string | null
    minParticipants?: number | null
    confirmedCount?: number | null
    resourceName?: string | null
    creditsRequired?: number | null
    subscriptionCreditsUsed?: number | null
    balanceCreditsUsed?: number | null
}

// ... (props default)

type SessionDetailDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    session: SessionDetailData | null
    /** Dialog title, e.g. "Kurs buchen" or "Kursdetails" */
    title?: string
    /** Dialog description shown below the title */
    description?: string
    /** Footer content — if omitted a default "Schließen" button is shown */
    footer?: React.ReactNode
    /** If provided, shows a "Stornieren" button and calls this handler */
    onCancel?: () => void
    /** If true, the cancel button shows a loading state */
    cancelPending?: boolean
    /** If true, shows a link to the trainer detail page (Admin only) */
    isAdmin?: boolean
    /** If false, ignores cancellation deadlines when deciding if the cancel action is allowed */
    enforceCancellationDeadline?: boolean
}

export function SessionDetailDialog({
    open,
    onOpenChange,
    session,
    title = 'Kursdetails',
    description,
    footer,
    onCancel,
    cancelPending,
    isAdmin = false,
    enforceCancellationDeadline = true,
}: SessionDetailDialogProps) {
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)

    if (!session) return null

    const toValidDate = (value?: string | null) => {
        if (!value) return null
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }

    const startsAtDate = toValidDate(session.startsAt)
    const endsAtDate = toValidDate(session.endsAt)
    const cancellationUntilDate = toValidDate(session.cancellationUntil)
    const courseDescription = session.courseDescription?.trim()
        ? session.courseDescription.trim()
        : null

    const formatDeadline = (value?: string | null) => {
        const deadline = toValidDate(value)
        return deadline
            ? format(deadline, "dd.MM.yyyy 'um' HH:mm", {
                  locale: de,
              })
            : '-'
    }

    const statusLabel =
        session.status === 'confirmed'
            ? 'Bestätigt'
            : session.status === 'cancelled'
                ? 'Storniert'
                : null

    const statusVariant =
        session.status === 'confirmed'
            ? 'default'
            : session.status === 'cancelled'
                ? 'destructive'
                : ('secondary' as const)

    const canCancel =
        onCancel &&
        session.status === 'confirmed' &&
        startsAtDate !== null &&
        isAfter(startsAtDate, new Date()) &&
        (
            !enforceCancellationDeadline ||
            !cancellationUntilDate ||
            isAfter(cancellationUntilDate, new Date())
        )

    const isCancellationDeadlinePassed =
        Boolean(onCancel) &&
        session.status === 'confirmed' &&
        enforceCancellationDeadline &&
        startsAtDate !== null &&
        isAfter(startsAtDate, new Date()) &&
        cancellationUntilDate !== null &&
        !isAfter(cancellationUntilDate, new Date())

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                ref={contentRef}
                onOpenAutoFocus={(event) => {
                    event.preventDefault()
                    requestAnimationFrame(() => {
                        contentRef.current?.focus({ preventScroll: true })
                    })
                }}
                onCloseAutoFocus={(event) => {
                    event.preventDefault()
                }}
                tabIndex={-1}
                className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden sm:max-w-md"
            >
                <DialogHeader className="shrink-0">
                    <DialogTitle>{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    {session.sessionId && <CourseParticipationInfo sessionId={session.sessionId} />}
                    <div className="my-2 space-y-3 rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold text-lg">{session.courseName}</p>
                            {statusLabel && <Badge variant={statusVariant}>{statusLabel}</Badge>}
                        </div>

                        <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 shrink-0" />
                                <span className="text-sm">
                                    {startsAtDate
                                        ? format(startsAtDate, "EEEE, dd. MMMM yyyy", {
                                              locale: de,
                                          })
                                        : 'Termin nicht verfuegbar'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <ClockIcon className="h-4 w-4 shrink-0" />
                                <span className="text-sm">
                                    {startsAtDate && endsAtDate
                                        ? `${format(startsAtDate, 'HH:mm')} – ${format(endsAtDate, 'HH:mm')} Uhr`
                                        : 'Uhrzeit nicht verfuegbar'}
                                </span>
                            </div>
                            {session.availableSpots != null && session.maxParticipants != null && (
                                <div className="flex items-center gap-2">
                                    <UsersIcon className="h-4 w-4 shrink-0" />
                                    <span className="text-sm">
                                        {session.availableSpots === 1
                                            ? '1 Platz frei'
                                            : `${session.availableSpots} Plätze frei`}
                                    </span>
                                </div>
                            )}
                            {session.creditsRequired != null && (
                                <div className="flex items-start gap-2">
                                    <CoinsIcon className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span className="text-sm">
                                        Kurswert: <strong>{formatCourseCredits(session.creditsRequired)}</strong>
                                        {session.sessionId && <CourseCreditTypeName sessionId={session.sessionId} />}
                                    </span>
                                </div>
                            )}
                            {session.trainerName && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm">
                                        Trainer:{' '}
                                        {session.trainerId && isAdmin ? (
                                            <Link
                                                to={`/admin/trainer/${session.trainerId}`}
                                                className="font-bold text-primary hover:underline"
                                                onClick={() => onOpenChange(false)}
                                            >
                                                {isAdmin ? session.trainerName : session.trainerName?.split(' ')[0]}
                                            </Link>
                                        ) : (
                                            <strong>{isAdmin ? session.trainerName : session.trainerName?.split(' ')[0]}</strong>
                                        )}
                                    </span>
                                </div>
                            )}
                            {session.resourceName && (
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    <span className="text-sm">
                                        Ort:{' '}
                                        <strong>{session.resourceName}</strong>
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <span className="text-sm">
                                    Anmeldung möglich bis:{' '}
                                    <strong>{formatDeadline(session.registrationUntil)}</strong>
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm">
                                    Stornierung möglich bis:{' '}
                                    <strong>{formatDeadline(session.cancellationUntil)}</strong>
                                </span>
                            </div>
                        </div>

                        {session.status === 'confirmed' &&
                            ((session.subscriptionCreditsUsed ?? 0) > 0 ||
                                (session.balanceCreditsUsed ?? 0) > 0) && (
                                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                                    <div className="flex items-start gap-2.5">
                                        <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 text-primary">
                                            <CoinsIcon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-semibold text-foreground">
                                                    Credit-Abrechnung
                                                </p>
                                                <span className="shrink-0 rounded-full border border-primary/20 bg-background px-2 py-0.5 text-xs font-semibold text-foreground">
                                                    {formatCourseCredits(
                                                        session.creditsRequired ??
                                                            (session.subscriptionCreditsUsed ?? 0) +
                                                                (session.balanceCreditsUsed ?? 0),
                                                    )}
                                                </span>
                                            </div>
                                            <div
                                                className={cn(
                                                    'mt-2 grid gap-2',
                                                    (session.subscriptionCreditsUsed ?? 0) > 0 &&
                                                        (session.balanceCreditsUsed ?? 0) > 0
                                                        ? 'grid-cols-2'
                                                        : 'grid-cols-1',
                                                )}
                                            >
                                                {(session.subscriptionCreditsUsed ?? 0) > 0 && (
                                                    <div className="rounded-md border bg-background/80 px-2.5 py-2">
                                                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                                            Aus Abo
                                                        </p>
                                                        <p className="mt-0.5 text-sm font-semibold text-foreground">
                                                            {formatCredits(
                                                                session.subscriptionCreditsUsed ?? 0,
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                                {(session.balanceCreditsUsed ?? 0) > 0 && (
                                                    <div className="rounded-md border bg-background/80 px-2.5 py-2">
                                                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                                            Aus Guthaben
                                                        </p>
                                                        <p className="mt-0.5 text-sm font-semibold text-foreground">
                                                            {formatCredits(
                                                                session.balanceCreditsUsed ?? 0,
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        {courseDescription && (
                            <Collapsible
                                key={`${session.courseName}-${session.startsAt}-${session.status ?? 'none'}`}
                                className="space-y-2"
                                defaultOpen={false}
                            >
                                <CollapsibleTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="group h-auto w-full justify-start px-0 py-1 text-left hover:bg-transparent"
                                    >
                                        <span className="text-sm font-semibold text-primary">
                                            Details ansehen
                                        </span>
                                        <ChevronDown
                                            className={cn(
                                                'ml-1 h-4 w-4 shrink-0 text-primary transition-transform duration-200',
                                                'group-data-[state=open]:rotate-180',
                                            )}
                                        />
                                    </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <p className="pt-1 text-sm leading-relaxed text-foreground/80 whitespace-pre-line">
                                        {courseDescription}
                                    </p>
                                </CollapsibleContent>
                            </Collapsible>
                        )}

                        {session.status === 'confirmed' && startsAtDate && endsAtDate && (
                            <div className="pt-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full gap-2 border-dashed hover:border-solid transition-all"
                                    onClick={() => downloadIcsFile({
                                        title: session.courseName,
                                        start: session.startsAt,
                                        end: session.endsAt,
                                        location: session.resourceName ?? undefined,
                                        description: [
                                            courseDescription,
                                            `Trainer: ${session.trainerName ?? '-'}`,
                                        ].filter(Boolean).join('\n\n')
                                    })}
                                >
                                    <CalendarPlusIcon className="h-4 w-4" />
                                    <span>Zum Kalender hinzufügen</span>
                                </Button>
                            </div>
                        )}
                    </div>
                    {session.bookingId && <BookingGuestsPanel bookingId={session.bookingId} active={session.status === 'confirmed' && !!startsAtDate && startsAtDate > new Date()} />}
                </div>

                <DialogFooter className="shrink-0 gap-2 sm:gap-0">
                    {footer ?? (
                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {isCancellationDeadlinePassed ? (
                                <p className="text-xs text-muted-foreground sm:flex-1">
                                    Stornierungsfrist abgelaufen
                                </p>
                            ) : (
                                <div className="hidden sm:block sm:flex-1" />
                            )}
                            <div className="flex w-full justify-end gap-2 sm:w-auto sm:shrink-0">
                                {canCancel && (
                                    <Button
                                        variant="destructive"
                                        onClick={() => setConfirmCancelOpen(true)}
                                        disabled={cancelPending}
                                    >
                                        {cancelPending ? (
                                            <>
                                                <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                                                Wird storniert...
                                            </>
                                        ) : (
                                            'Stornieren'
                                        )}
                                    </Button>
                                )}
                                <Button variant="outline" onClick={() => onOpenChange(false)}>
                                    Schließen
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogFooter>
            </DialogContent>
            <CancelBookingConfirmDialog
                open={open && confirmCancelOpen}
                onOpenChange={setConfirmCancelOpen}
                label={[session.courseName, startsAtDate && format(startsAtDate, "EEEE, dd.MM.yyyy 'um' HH:mm", { locale: de })]
                    .filter(Boolean)
                    .join(' · ')}
                onConfirm={() => {
                    setConfirmCancelOpen(false)
                    onCancel?.()
                }}
            />
        </Dialog>
    )
}
